/**
 * Schema.org structured data utilities for SEO
 * Generates JSON-LD schema markup for different page types
 */

/**
 * Injects schema into page head
 * @param {Object|Array} schema - Schema object or array to inject
 * @param {string} id - Unique identifier for the script tag
 */
export const injectSchema = (schema, id = 'schema-org') => {
  // Remove existing schema with same id
  const existingScript = document.getElementById(id);
  if (existingScript) {
    existingScript.remove();
  }

  // Create and inject new schema
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.text = JSON.stringify(schema);
  document.head.appendChild(script);
};

/**
 * Removes schema from page head
 * @param {string} id - Unique identifier for the script tag
 */
export const removeSchema = (id = 'schema-org') => {
  const existingScript = document.getElementById(id);
  if (existingScript) {
    existingScript.remove();
  }
};

/**
 * Base organization schema for Hubzu
 */
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Hubzu",
  "url": "https://www.hubzu.com",
  "logo": "https://www.hubzu.com/assets/images/hubzu-logo.png",
  "description": "Real estate auction platform for buying and selling properties",
  "sameAs": [
    "https://www.facebook.com/hubzu",
    "https://www.instagram.com/hubzu_official",
    "https://www.linkedin.com/company/hubzu"
  ]
});

/**
 * Website schema - without search action since no general search route exists
 */
export const getWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Hubzu",
  "url": "https://www.hubzu.com"
});

/**
 * Generate home page schema
 * @param {Object} cmsData - CMS data from API (cmsData.Sections)
 * @returns {Object} Organization schema
 */
export const generateHomePageSchema = (cmsData) => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Hubzu",
    "url": "https://www.hubzu.com",
    "logo": "https://www.hubzu.com/assets/images/hubzu-logo.png",
    "description": "Real estate auction platform for buying and selling properties",
    "sameAs": [
      "https://www.facebook.com/hubzu",
      "https://www.instagram.com/hubzu_official",
      "https://www.linkedin.com/company/hubzu"
    ]
  };
};

/**
 * Generate property listing schema for SRP
 * @param {Array} properties - Array of property objects from API
 * @returns {Object} ItemList schema
 */
export const generatePropertyListSchema = (properties) => {
  if (!properties || properties.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": properties.map((property, index) => {
      const address = property.propAddress;
      const fullAddress = `${address.streetNumber} ${address.streetName}, ${address.city}, ${address.state} ${address.zip}`;
      
      return {
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": fullAddress,
          "url": `https://www.hubzu.com${property.listingUrl}`,
          "image": property.imageUrl || "https://www.hubzu.com/assets/images/hubzu-logo.png",
          "description": `${property.bedCount} bed, ${property.bathCount} bath ${property.propertySubType} in ${address.city}, ${address.state}`,
          "offers": {
            "@type": "Offer",
            "price": (property.currentBid || property.startingBid || property.listingPrice || "0").replace(/,/g, ''),
            "priceCurrency": "USD",
            "availability": property.listingStatus === "Active" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
          }
        }
      };
    })
  };
};

/**
 * Generate single property schema for PDP
 * @param {Object} propertyData - Property data from API (full response object)
 * @returns {Array} Array of schema objects
 */
export const generatePropertyDetailSchema = (propertyData) => {
  if (!propertyData?.propertyDetails) return null;

  const { propertyDetails, listingDetail, listingAgentDetail } = propertyData;
  const { address, commonAttributes, detailedAttributes } = propertyDetails;

  const schemas = [];

  // Main property as Product schema (better Google support)
  const propertySchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `https://www.hubzu.com/property-details/${propertyDetails.propertyId}`,
    "name": `${address.streetNumber} ${address.streetName}, ${address.city}, ${address.state} ${address.zip}`,
    "description": detailedAttributes?.description || `${commonAttributes?.bedroomsNum || 0} bed, ${commonAttributes?.bathsNum || 0} bath property in ${address.city}, ${address.state}`,
    "url": `https://www.hubzu.com/property-details/${propertyDetails.propertyId}`,
    "category": "Real Estate",
    "brand": {
      "@type": "Brand",
      "name": "Hubzu"
    }
  };

  // Always add image (required by Google) - use property images or fallback
  if (propertyDetails.imageDetails && propertyDetails.imageDetails.length > 0) {
    propertySchema.image = propertyDetails.imageDetails.map(img => img.imageUrl || img.url);
  } else {
    // Fallback to Hubzu logo if no property images
    propertySchema.image = "https://www.hubzu.com/assets/images/hubzu-logo.png";
  }

  // Always add offers (required by Google)
  const price = listingDetail?.currentHighestPrice || listingDetail?.startingPrice || listingDetail?.listPrice || 0;
  propertySchema.offers = {
    "@type": "Offer",
    "price": price,
    "priceCurrency": "USD",
    "availability": listingDetail?.listingStatus === "Active" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    "url": `https://www.hubzu.com/property-details/${propertyDetails.propertyId}`,
    "seller": {
      "@type": "Organization",
      "name": "Hubzu"
    }
  };
  
  // Add priceValidUntil if available
  if (listingDetail?.endDate) {
    propertySchema.offers.priceValidUntil = new Date(listingDetail.endDate).toISOString().split('T')[0];
  }

  // Add additional properties as structured data
  const additionalProperty = [];
  if (commonAttributes?.bedroomsNum) {
    additionalProperty.push({
      "@type": "PropertyValue",
      "name": "Bedrooms",
      "value": commonAttributes.bedroomsNum
    });
  }
  if (commonAttributes?.bathsNum) {
    additionalProperty.push({
      "@type": "PropertyValue",
      "name": "Bathrooms",
      "value": commonAttributes.bathsNum
    });
  }
  if (detailedAttributes?.squareFeet) {
    additionalProperty.push({
      "@type": "PropertyValue",
      "name": "Square Feet",
      "value": detailedAttributes.squareFeet.replace(/[^0-9]/g, '')
    });
  }
  if (commonAttributes?.yearBuilt) {
    additionalProperty.push({
      "@type": "PropertyValue",
      "name": "Year Built",
      "value": commonAttributes.yearBuilt
    });
  }
  if (commonAttributes?.lotSize) {
    additionalProperty.push({
      "@type": "PropertyValue",
      "name": "Lot Size",
      "value": commonAttributes.lotSize
    });
  }
  if (commonAttributes?.subType) {
    additionalProperty.push({
      "@type": "PropertyValue",
      "name": "Property Type",
      "value": commonAttributes.subType
    });
  }
  if (additionalProperty.length > 0) {
    propertySchema.additionalProperty = additionalProperty;
  }

  schemas.push(propertySchema);

  // Add breadcrumb schema
  schemas.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.hubzu.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": address.state,
        "item": `https://www.hubzu.com${address.stateLandingUrl}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": address.city,
        "item": `https://www.hubzu.com${address.cityLandingUrl}`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": `${address.streetNumber} ${address.streetName}`,
        "item": `https://www.hubzu.com/property-details/${propertyDetails.propertyId}`
      }
    ]
  });

  // Add listing agent as Person schema if available
  if (listingAgentDetail && listingAgentDetail.listingAgentFirstName) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Person",
      "name": `${listingAgentDetail.listingAgentFirstName} ${listingAgentDetail.listingAgentLastName}`,
      "email": listingAgentDetail.listingAgentEmailId,
      "telephone": listingAgentDetail.listingAgentPhone,
      "jobTitle": "Listing Agent",
      "worksFor": {
        "@type": "Organization",
        "name": listingAgentDetail.agency || "Hubzu"
      }
    });
  }

  return schemas;
};

/**
 * Generate schema for sell commercial page
 * @returns {Array} Array of schema objects
 */
export const generateSellCommercialSchema = () => {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Commercial Property Selling Services",
      "description": "Sell your commercial properties through Hubzu's online auction platform. Fast, transparent, and efficient commercial real estate sales.",
      "provider": {
        "@type": "Organization",
        "name": "Hubzu",
        "url": "https://www.hubzu.com",
        "logo": "https://www.hubzu.com/assets/images/hubzu-logo.png"
      },
      "serviceType": "Commercial Real Estate Sales",
      "areaServed": "US",
      "url": "https://www.hubzu.com/sell-commercial"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.hubzu.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Sell Commercial",
          "item": "https://www.hubzu.com/sell-commercial"
        }
      ]
    }
  ];
};

/**
 * Generate schema for sell residential page
 * @returns {Array} Array of schema objects
 */
export const generateSellResidentialSchema = () => {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Residential Property Selling Services",
      "description": "Sell your residential properties through Hubzu's Signature Home Auctions. Fast, transparent, and efficient residential real estate sales.",
      "provider": {
        "@type": "Organization",
        "name": "Hubzu",
        "url": "https://www.hubzu.com",
        "logo": "https://www.hubzu.com/assets/images/hubzu-logo.png"
      },
      "serviceType": "Residential Real Estate Sales",
      "areaServed": "US",
      "url": "https://www.hubzu.com/sell-residential"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.hubzu.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Sell Residential",
          "item": "https://www.hubzu.com/sell-residential"
        }
      ]
    }
  ];
};
